import { FieldState } from 'src/Types';
import { useLocalization } from 'src/Hooks';
import { useAppRouting } from 'src/Hooks/useAppRouting';
import { useSelector } from 'react-redux';
import { salesPageSelectors } from 'src/Pages/Sales/services';
import { purchasesPageSelectors } from 'src/Pages/Purchases/services';
import { globalSelectors } from 'src/Redux/Global/selectors';
import { useMemo } from 'react';
import { localizationUtils } from 'src/Utils/localizationUtils';
import { orderTableRowUtils } from 'src/Types/OrderTableRowUtils';

export const useDeliveryNoteNumberTooltipMessage = (): FieldState => {
  const {
    models: {
      OrderTableRow: { DeliveryNoteNumber, parts, CommonErrors },
    },
  } = useLocalization();
  const { isSalesPage, isPurchasesPage } = useAppRouting();
  const selectedSales = useSelector(salesPageSelectors.selectedSales);
  const selectedPurchases = useSelector(purchasesPageSelectors.selectedPurchases);
  const localization = useSelector(globalSelectors.localization);

  return useMemo(() => {
    const orders = isSalesPage ? selectedSales : selectedPurchases;

    const result = orders.reduce(
      (res, current) => {
        if (current.deliveryNoteNumberCreatedTime) {
          res.withDeliveryNoteNumbers.add(current.deliveryNoteNumber ?? '');
        }
        if (orderTableRowUtils.isOrderIsAmazonPending(current)) {
          res.amazonInProcessOrders.add(current.userOrderId.toString());
        }
        return res;
      },
      {
        withDeliveryNoteNumbers: new Set<string>(),
        amazonInProcessOrders: new Set<string>(),
      },
    );

    const withDeliveryNoteNumbers = [...result.withDeliveryNoteNumbers];
    const amazonInProcessOrders = [...result.amazonInProcessOrders];

    let disabledDocumentMessage: string | undefined;

    if (withDeliveryNoteNumbers.length) {
      disabledDocumentMessage = localizationUtils.insertTranslatedParamsToString(
        DeliveryNoteNumber.DisableHeaderDocumentMessage,
        [withDeliveryNoteNumbers.join(', '), withDeliveryNoteNumbers.length],
        parts,
        localization,
      );
    } else if (amazonInProcessOrders.length) {
      disabledDocumentMessage = CommonErrors.AmazonOrderPending.DisableHeaderDocumentMessage;
    } // if

    return {
      value: '',
      isDocumentIconDisabled:
        Boolean(disabledDocumentMessage) || Boolean(withDeliveryNoteNumbers.length) || isPurchasesPage,
      disabledDocumentMessage,
    };
  }, [localization, selectedSales, selectedPurchases, isSalesPage, isPurchasesPage]);
};
